import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Edit, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { normalizeGreek } from '../utils/greekUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_GR = {
  1: 'ΙΑΝΟΥΑΡΙΟΣ', 2: 'ΦΕΒΡΟΥΑΡΙΟΣ', 3: 'ΜΑΡΤΙΟΣ', 4: 'ΑΠΡΙΛΙΟΣ',
  5: 'ΜΑΙΟΣ', 6: 'ΙΟΥΝΙΟΣ', 7: 'ΙΟΥΛΙΟΣ', 8: 'ΑΥΓΟΥΣΤΟΣ',
  9: 'ΣΕΠΤΕΜΒΡΙΟΣ', 10: 'ΟΚΤΩΒΡΙΟΣ', 11: 'ΝΟΕΜΒΡΙΟΣ', 12: 'ΔΕΚΕΜΒΡΙΟΣ'
};

const HistoryPage = () => {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc'); // desc = newest first
  
  // Edit section
  const [selectedReadingId, setSelectedReadingId] = useState('---');
  const [editYear, setEditYear] = useState(new Date().getFullYear());
  const [editMonth, setEditMonth] = useState(1);
  const [editBlack, setEditBlack] = useState(0);
  const [editColor, setEditColor] = useState(0);
  const [editA3, setEditA3] = useState(0);
  const [editA3Color, setEditA3Color] = useState(0);
  const [editPassword, setEditPassword] = useState('');
  
  // Delete section
  const [deleteId, setDeleteId] = useState(0);
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    fetchReadings();
  }, []);

  // Filtered and sorted readings
  const filteredReadings = useMemo(() => {
    let result = [...readings];
    
    // Filter by search term
    if (searchTerm.trim() !== '') {
      const normSearch = normalizeGreek(searchTerm).toLowerCase();
      result = result.filter(r => 
        normalizeGreek(r.company_name || '').toLowerCase().includes(normSearch) ||
        normalizeGreek(r.machine_serial || '').toLowerCase().includes(normSearch)
      );
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      // Handle null/undefined
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // Numeric comparison for numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison with Greek normalization
      const strA = normalizeGreek(String(aVal)).toLowerCase();
      const strB = normalizeGreek(String(bVal)).toLowerCase();
      
      if (sortDirection === 'asc') {
        return strA.localeCompare(strB, 'el');
      } else {
        return strB.localeCompare(strA, 'el');
      }
    });
    
    return result;
  }, [readings, searchTerm, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to desc for ID, asc for others
      setSortColumn(column);
      setSortDirection(column === 'id' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-blue-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-blue-600" />;
  };

  const SortableHeader = ({ column, children, className = '' }) => (
    <th 
      className={`p-2 cursor-pointer hover:bg-gray-200 transition-colors select-none ${className}`}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center justify-center">
        {children}
        <SortIcon column={column} />
      </div>
    </th>
  );

  const fetchReadings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/readings/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadings(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης ιστορικού');
    } finally {
      setLoading(false);
    }
  };

  const handleReadingSelect = (value) => {
    setSelectedReadingId(value);
    
    if (value === '---') {
      setEditYear(new Date().getFullYear());
      setEditMonth(1);
      setEditBlack(0);
      setEditColor(0);
      setEditA3(0);
      setEditA3Color(0);
      return;
    }
    
    const reading = readings.find(r => r.id === parseInt(value));
    if (reading) {
      setEditYear(reading.reading_year);
      setEditMonth(reading.reading_month);
      setEditBlack(reading.counter_value_black);
      setEditColor(reading.counter_value_color);
      setEditA3(reading.counter_value_a3 || 0);
      setEditA3Color(reading.counter_value_a3_color || 0);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedReadingId === '---') {
      toast.error('Επιλέξτε εγγραφή');
      return;
    }
    
    if (!editPassword) {
      toast.error('Εισάγετε κωδικό!');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/readings/${selectedReadingId}`,
        {
          reading_year: editYear,
          reading_month: editMonth,
          counter_value_black: editBlack,
          counter_value_color: editColor,
          counter_value_a3: editA3,
          counter_value_a3_color: editA3Color,
          admin_password: editPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Η ΕΓΓΡΑΦΗ #${selectedReadingId} ΕΝΗΜΕΡΩΘΗΚΕ!`);
      setSelectedReadingId('---');
      setEditPassword('');
      fetchReadings();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης: ' + (error.response?.data?.detail || ''));
    }
  };

  const handleDelete = async () => {
    if (deleteId === 0) {
      toast.error('Συμπληρώστε ID εγγραφής');
      return;
    }
    
    if (!deletePassword) {
      toast.error('Εισάγετε κωδικό!');
      return;
    }
    
    const exists = readings.find(r => r.id === deleteId);
    if (!exists) {
      toast.error('ΤΟ ID ΔΕΝ ΒΡΕΘΗΚΕ');
      return;
    }
    
    if (!window.confirm(`Επιβεβαίωση διαγραφής εγγραφής #${deleteId};`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/readings/${deleteId}?admin_password=${deletePassword}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Η ΕΓΓΡΑΦΗ ${deleteId} ΔΙΑΓΡΑΦΗΚΕ!`);
      setDeleteId(0);
      setDeletePassword('');
      fetchReadings();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής: ' + (error.response?.data?.detail || ''));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12 text-lg">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-6 pb-8">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900">📊 ΙΣΤΟΡΙΚΟ & ΔΙΑΧΕΙΡΙΣΗ ΜΕΤΡΗΣΕΩΝ</h1>
        <p className="text-gray-600 mt-1">Προβολή και επεξεργασία καταχωρημένων μετρήσεων</p>
      </div>

      {/* Search Section */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-gray-500" />
            <Input
              placeholder="🔍 ΑΝΑΖΗΤΗΣΗ ΠΕΛΑΤΗ Η Serial"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>
        </CardContent>
      </Card>

      {/* Readings Table */}
      <Card>
        <CardContent className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <SortableHeader column="id" className="text-left">ID</SortableHeader>
                  <SortableHeader column="company_name" className="text-left">ΠΕΛΑΤΗΣ</SortableHeader>
                  <SortableHeader column="brand" className="text-left">ΜΑΡΚΑ</SortableHeader>
                  <SortableHeader column="model" className="text-left">ΜΟΝΤΕΛΟ</SortableHeader>
                  <SortableHeader column="machine_serial" className="text-left">Serial</SortableHeader>
                  <SortableHeader column="reading_month" className="text-center">ΜΗΝΑΣ</SortableHeader>
                  <SortableHeader column="reading_year" className="text-center">ΕΤΟΣ</SortableHeader>
                  <SortableHeader column="pages_black" className="text-right">ΣΕΛΙΔΕΣ A4 Black</SortableHeader>
                  <SortableHeader column="pages_color" className="text-right">ΣΕΛΙΔΕΣ A4 Color</SortableHeader>
                  <SortableHeader column="pages_a3" className="text-right">ΣΕΛΙΔΕΣ A3 Black</SortableHeader>
                  <SortableHeader column="pages_a3_color" className="text-right">ΣΕΛΙΔΕΣ A3 Color</SortableHeader>
                  <SortableHeader column="date_recorded" className="text-center">ΗΜΕΡΟΜΗΝΙΑ</SortableHeader>
                </tr>
              </thead>
              <tbody>
                {filteredReadings.length > 0 ? (
                  filteredReadings.map((r, idx) => (
                    <tr key={r.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-2 font-semibold">{r.id}</td>
                      <td className="p-2">{r.company_name}</td>
                      <td className="p-2">{r.brand}</td>
                      <td className="p-2">{r.model}</td>
                      <td className="p-2 font-mono text-sm">{r.machine_serial}</td>
                      <td className="p-2 text-center">{MONTHS_GR[r.reading_month]}</td>
                      <td className="p-2 text-center">{r.reading_year}</td>
                      <td className="p-2 text-right font-semibold">{r.pages_black?.toLocaleString()}</td>
                      <td className="p-2 text-right font-semibold">{r.pages_color?.toLocaleString()}</td>
                      <td className="p-2 text-right font-semibold">{r.pages_a3?.toLocaleString() || 0}</td>
                      <td className="p-2 text-right font-semibold">{r.pages_a3_color?.toLocaleString() || 0}</td>
                      <td className="p-2 text-center text-xs">{r.date_recorded?.substring(0, 10)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={12} className="p-8 text-center text-gray-500">
                      Δεν βρέθηκαν εγγραφές
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            Σύνολο εγγραφών: <span className="font-semibold">{filteredReadings.length}</span>
            {searchTerm && <span> (από {readings.length})</span>}
          </div>
        </CardContent>
      </Card>

      {/* Edit Section */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500 rounded-lg">
              <Edit className="h-6 w-6 text-white" />
            </div>
            <CardTitle>✏️ ΔΙΟΡΘΩΣΗ ΕΓΓΡΑΦΗΣ ΜΕΤΡΗΣΗΣ</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {readings.length > 0 ? (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">ΕΠΙΛΕΞΤΕ ΕΓΓΡΑΦΗ ΓΙΑ ΔΙΟΡΘΩΣΗ</label>
                <select
                  value={selectedReadingId}
                  onChange={(e) => handleReadingSelect(e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="---">---</option>
                  {[...readings].sort((a, b) => b.id - a.id).map(r => (
                    <option key={r.id} value={r.id}>
                      #{r.id} | {r.company_name} | {r.brand} {r.model} | {r.machine_serial} | {MONTHS_GR[r.reading_month]} {r.reading_year}
                    </option>
                  ))}
                </select>
              </div>

              {selectedReadingId !== '---' && (
                <form onSubmit={handleEditSubmit} className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <div className="bg-blue-100 p-3 rounded border border-blue-300 text-sm">
                    <strong>ΔΙΟΡΘΩΣΗ ΕΓΓΡΑΦΗΣ #{selectedReadingId}</strong>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">ΕΤΟΣ</label>
                      <Input
                        type="number"
                        value={editYear}
                        onChange={(e) => setEditYear(parseInt(e.target.value))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">ΜΗΝΑΣ</label>
                      <select
                        value={editMonth}
                        onChange={(e) => setEditMonth(parseInt(e.target.value))}
                        className="w-full p-2 border rounded"
                      >
                        {Object.entries(MONTHS_GR).map(([num, name]) => (
                          <option key={num} value={num}>{name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">A4 Black</label>
                      <Input
                        type="number"
                        value={editBlack}
                        onChange={(e) => setEditBlack(parseInt(e.target.value))}
                        min={0}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">A4 Color</label>
                      <Input
                        type="number"
                        value={editColor}
                        onChange={(e) => setEditColor(parseInt(e.target.value))}
                        min={0}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">A3 Black</label>
                      <Input
                        type="number"
                        value={editA3}
                        onChange={(e) => setEditA3(parseInt(e.target.value))}
                        min={0}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">A3 Color</label>
                      <Input
                        type="number"
                        value={editA3Color}
                        onChange={(e) => setEditA3Color(parseInt(e.target.value))}
                        min={0}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">ΚΩΔΙΚΟΣ Admin</label>
                    <Input
                      type="password"
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      placeholder="Εισάγετε κωδικό admin"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                    💾 ΑΠΟΘΗΚΕΥΣΗ ΑΛΛΑΓΩΝ
                  </Button>
                </form>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-center py-4">Δεν υπάρχουν εγγραφές για επεξεργασία</p>
          )}
        </CardContent>
      </Card>

      {/* Delete Section */}
      <Card className="border-l-4 border-l-red-500">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-500 rounded-lg">
              <Trash2 className="h-6 w-6 text-white" />
            </div>
            <CardTitle>🗑️ ΔΙΑΓΡΑΦΗ ΛΑΝΘΑΣΜΕΝΗΣ ΚΑΤΑΓΡΑΦΗΣ</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">ID ΕΓΓΡΑΦΗΣ</label>
              <Input
                type="number"
                value={deleteId || ''}
                onChange={(e) => setDeleteId(parseInt(e.target.value) || 0)}
                placeholder="0"
                min={0}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">ΚΩΔΙΚΟΣ Admin</label>
              <Input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Κωδικός"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="w-full"
              >
                ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoryPage;
